import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import screenfull from 'screenfull';
import preloadImage from '@99designs/utils/network/preloadImage';
import Fullscreen from '@99designs/fullscreen';
import AdminBarStatusProvider from './AdminBarStatusProvider';
import { ConnectedDesignDetailsHeader } from './Header/DesignDetailsHeader';
import { ConnectedSidebarComponent } from './DesignDetailsSidebar';
import ZoomLevels from '../constants/zoom-level-constants';
import { getAvailableZoomLevels } from '../lib/zoom';
import Desktop from '@99designs/react-components/components/Desktop';
import Mobile from '@99designs/react-components/components/Mobile';
import MediaQuery from 'react-responsive';
import breakpoints from '@99designs/asimov/platform-settings/settings/breakpoints';
import BlankSlate from './BlankSlate';
import PreviewAlert from './PreviewAlert';
import DesignPane from './DesignPane/DesignPane';

const msUntilPreloadPagination = 5000;

export default class AppComponent extends React.Component {
    constructor() {
        super();

        this.inactiveUiTimer = null;
        this.paginationPreloadTimer = null;

        this.state = {
            isPresentationMode: false,
            zoomLevel: ZoomLevels.FIT_TO_CONTAINER,
            availableZoomLevels: [],
            viewSize: null,
            feedbackMode: 'annotate',
        };

        this.clearDesignCommentSelection = this.clearDesignCommentSelection.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleMaximize = this.handleMaximize.bind(this);
        this.handleMinimize = this.handleMinimize.bind(this);
        this.handleFullscreenChange = this.handleFullscreenChange.bind(this);
        this.closeOverlayHandler = this.closeOverlayHandler.bind(this);
        this.zoomIn = this.zoomIn.bind(this);
        this.zoomOut = this.zoomOut.bind(this);
    }

    componentDidMount() {
        window.addEventListener('keydown', this.handleKeyDown);
        if (screenfull.enabled) {
            window.addEventListener(screenfull.raw.fullscreenchange, this.handleFullscreenChange);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        window.clearTimeout(this.paginationPreloadTimer);
        this.paginationPreloadTimer = window.setTimeout(() => {
            if (this.props.nextDesign) {
                preloadImage(this.props.nextDesign.previewUrl);
            }
            if (this.props.previousDesign) {
                preloadImage(this.props.previousDesign.previewUrl);
            }
        }, msUntilPreloadPagination);
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleKeyDown);
        if (screenfull.enabled) {
            window.removeEventListener(
                screenfull.raw.fullscreenchange,
                this.handleFullscreenChange
            );
        }

        window.clearTimeout(this.inactiveUiTimer);
        window.clearTimeout(this.paginationPreloadTimer);
    }

    setZoomLevel(targetZoomLevel) {
        if (!this.state.viewSize) {
            return null;
        }

        const designDimensions = {
            width: this.props.design.width,
            height: this.props.design.height,
        };

        const availableZoomLevels = getAvailableZoomLevels(designDimensions, this.state.viewSize);

        const isValidTargetZoom =
            !targetZoomLevel || availableZoomLevels.indexOf(targetZoomLevel) === -1;
        const zoomLevel = isValidTargetZoom
            ? this.getDefaultZoomLevel(availableZoomLevels)
            : targetZoomLevel;

        this.setState({
            availableZoomLevels,
            zoomLevel,
        });
    }

    getDefaultZoomLevel(availableZoomLevels) {
        if (availableZoomLevels.indexOf(ZoomLevels.FIT_TO_CONTAINER) > -1) {
            return ZoomLevels.FIT_TO_CONTAINER;
        } else if (availableZoomLevels.indexOf(ZoomLevels.FIT_TO_NATIVE) > -1) {
            return ZoomLevels.FIT_TO_NATIVE;
        } else {
            return ZoomLevels.FLUID;
        }
    }

    zoomOut() {
        const level = this.getPrevZoomLevel();
        if (level) {
            this.setZoomLevel(level);
        }
    }

    zoomIn() {
        const level = this.getNextZoomLevel();
        if (level) {
            this.setZoomLevel(level);
        }
    }

    canZoomIn() {
        return Boolean(this.getNextZoomLevel());
    }

    canZoomOut() {
        return Boolean(this.getPrevZoomLevel());
    }

    getNextZoomLevel() {
        const index = this.state.availableZoomLevels.indexOf(this.state.zoomLevel);
        const nextIndex = index + 1;
        return this.state.availableZoomLevels[nextIndex]
            ? this.state.availableZoomLevels[nextIndex]
            : null;
    }

    getPrevZoomLevel() {
        const index = this.state.availableZoomLevels.indexOf(this.state.zoomLevel);
        const prevIndex = index - 1;
        return this.state.availableZoomLevels[prevIndex]
            ? this.state.availableZoomLevels[prevIndex]
            : null;
    }

    closeOverlayHandler() {
        if (document.querySelector('body[data-modal-status="active"]') === null) {
            // Both the modal and the overlay are listening for ESC events
            if (this.props.isInline) {
                window.setTimeout(() => {
                    this.props.goBack();
                }, this.props.onCloseDelayDuration);
            }
        }
        window.dispatchEvent(new CustomEvent('ddoDidClose'));
    }

    isKeypressInTextarea(e) {
        return e.target && e.target.nodeName === 'TEXTAREA';
    }

    isArrowKeypress(e) {
        return [39, 37].indexOf(e.keyCode) > -1;
    }

    handleEsc(e) {
        if (this.isKeypressInTextarea(e)) {
            this.clearDesignCommentSelection();
            ReactDOM.findDOMNode(this).focus();
        } else if (this.props.selectedComment && this.props.selectedComment.annotation) {
            this.clearDesignCommentSelection();
        } else if (this.state.isPresentationMode) {
            this.handleMinimize();
        } else {
            this.closeOverlayHandler();
        }
    }

    handleArrowKeys(e) {
        if (!this.isKeypressInTextarea(e)) {
            // left
            if (e.keyCode === 37) {
                this.props.goToPrevious();
            }

            // right
            if (e.keyCode === 39) {
                this.props.goToNext();
            }
        }
    }

    handleKeyDown(e) {
        if (!this.isKeypressInTextarea(e)) {
            // "+" key
            if (e.shiftKey && e.keyCode === 187) {
                return this.zoomIn();
            }

            // "-" key
            if (e.keyCode === 189) {
                return this.zoomOut();
            }
        }

        // ESC
        if (e.keyCode === 27) {
            return this.handleEsc(e);
        }

        if (this.isArrowKeypress(e)) {
            return this.handleArrowKeys(e);
        }
    }

    clearDesignCommentSelection() {
        if (this.props.selectedComment) {
            this.props.clearDesignCommentSelection();
        }
    }

    handleMaximize() {
        if (screenfull.enabled) {
            screenfull.request();
        }

        this.setState(() => ({
            isPresentationMode: true,
        }));
    }

    handleFullscreenChange(x) {
        if (screenfull.enabled) {
            this.setState(() => ({
                isPresentationMode: screenfull.isFullscreen,
            }));
        }
    }

    handleMinimize() {
        if (screenfull.enabled) {
            screenfull.exit();
        }

        this.setState(() => ({
            isPresentationMode: false,
        }));
    }

    render() {
        return (
            <AdminBarStatusProvider
                render={hasAdminBarOffset => (
                    <div
                        style={{ height: '100%', width: '100%', position: 'absolute' }}
                        id="design-details-annotations-anchor"
                    >
                        <Fullscreen.Overlay
                            onClick={this.clearDesignCommentSelection}
                            isActive={true}
                            isScrollableSmallScreens={true}
                            handleKeyDown={this.handleKeyDown}
                            enableScroll={this.props.isInline}
                            closeOverlayHandler={this.closeOverlayHandler}
                            hasAdminBarOffset={hasAdminBarOffset}
                        >
                            <Fullscreen.Frame.Frame isSidebarHidden={this.state.isPresentationMode}>
                                <Fullscreen.Frame.Main>
                                    <Fullscreen.Primary.Main hasHeader={true}>
                                        <Fullscreen.Primary.Header>
                                            <ConnectedDesignDetailsHeader
                                                feedbackMode={this.state.feedbackMode}
                                                toggleFeedbackMode={feedbackMode =>
                                                    this.setState({ feedbackMode })
                                                }
                                                design={this.props.design}
                                                zoomIn={this.canZoomIn() ? this.zoomIn : null}
                                                zoomOut={this.canZoomOut() ? this.zoomOut : null}
                                                goToPrevious={this.props.goToPrevious}
                                                goToNext={this.props.goToNext}
                                                closeOverlayHandler={this.closeOverlayHandler}
                                                handleMaximize={this.handleMaximize}
                                                handleMinimize={this.handleMinimize}
                                                isPresentationMode={this.state.isPresentationMode}
                                                isInline={this.props.isInline}
                                                seeMoreDesignsUrl={
                                                    this.props.headerSeeMoreDesignsUrl
                                                }
                                            />
                                        </Fullscreen.Primary.Header>
                                        <Fullscreen.Primary.Body variations={['design-details']}>
                                            <PreviewAlert design={this.props.design} />
                                            {this.renderDesignPane()}
                                            <BlankSlate design={this.props.design} />
                                            {this.props.renderDesignCanvasOverlay &&
                                                this.props.renderDesignCanvasOverlay({
                                                    design: this.props.design,
                                                })}
                                        </Fullscreen.Primary.Body>
                                    </Fullscreen.Primary.Main>
                                </Fullscreen.Frame.Main>
                                <Fullscreen.Frame.Sidebar>
                                    {this.renderSidebar()}
                                </Fullscreen.Frame.Sidebar>
                            </Fullscreen.Frame.Frame>
                        </Fullscreen.Overlay>
                    </div>
                )}
            />
        );
    }

    renderSidebar() {
        return (
            <MediaQuery minWidth={breakpoints.normal[0]}>
                {isDesktop => {
                    if (isDesktop || (!isDesktop && this.state.feedbackMode != 'sketch')) {
                        return (
                            <ConnectedSidebarComponent
                                renderSidebar={this.props.renderSidebar}
                                goToNext={this.props.goToNext}
                                goToPrevious={this.props.goToPrevious}
                                design={this.props.design}
                                isInline={this.props.isInline}
                                closeOverlayHandler={this.closeOverlayHandler}
                            />
                        );
                    }
                    return null;
                }}
            </MediaQuery>
        );
    }

    renderDesignPane() {
        if (!this.props.design.permission.canView || !this.props.design.width) return null;

        return (
            <DesignPane
                feedbackMode={this.state.feedbackMode}
                isPresentationMode={this.state.isPresentationMode}
                zoomLevel={this.state.zoomLevel}
                onSize={size =>
                    this.setState({ viewSize: size }, () => this.setZoomLevel(this.state.zoomLevel))
                }
                design={this.props.design}
            />
        );
    }
}

AppComponent.propTypes = {
    design: PropTypes.object.isRequired,
    goBack: PropTypes.func.isRequired,
    goToNext: PropTypes.func.isRequired,
    goToPrevious: PropTypes.func.isRequired,
    selectedComment: PropTypes.object,
    nextDesign: PropTypes.object,
    previousDesign: PropTypes.object,
    isInline: PropTypes.bool,
    onCloseDelayDuration: PropTypes.number,
    renderSidebar: PropTypes.func.isRequired,
    renderDesignCanvasOverlay: PropTypes.func, // TODO provide this to DesignPane
    headerSeeMoreDesignsUrl: PropTypes.string,
    clearDesignCommentSelection: PropTypes.func,
};

AppComponent.defaultProps = {
    nextDesign: null,
    previousDesign: null,
    isInline: true,
    hasFocusedComment: false,
    onCloseDelayDuration: 230,
};



// WEBPACK FOOTER //
// ./~/@99designs/ddo/components/AppComponent.jsx